import { selMessage } from "@selector/shared";
import { Component, createRef } from "react";
import { connect } from "react-redux";
import {
  type Field,
  Form,
  formValueSelector,
  type InjectedFormProps,
  reduxForm,
  SubmissionError,
  type Validator,
} from "redux-form";

import ExportedWorkflowFieldMapper, {
  ExportedWorkflowFieldMapperReduxFormField,
  type MapperWorkflowField,
} from "@/ui-modules/playbooks/editor/ExportedWorkflowFieldMapper.tsx";

import {
  getFormError,
  getFormWarning,
  getSelectedOption,
  handleTabInForm,
  simpleDropdownFieldValidation,
} from "../../../app-utils/form.js";
import {
  attributeFileTypeMatchesNfParamSubType,
  extractAttributeIdsFromMappingSelection,
  fleshOutConfiguredAttributes,
  getMatchingAttributeTypeFromNfParamType,
  getMatchingAttributeTypeFromParamUsage,
  type IConfiguredAttribute,
  isNgsDataFileAttribute,
  isRawDataFileSubType,
  isRawDataFileTypeNfParam,
  NfParamConfigToMapperWorkflowField,
} from "../../../app-utils/playbook.js";
import {
  type Attribute,
  type ExportWorkflowField,
  type InputWorkflowParamAttributeMapping,
  type Maybe,
  NfParamTypeEnum,
  type Playbook,
  type RawAttributeDefault,
  type SampleType,
  type Scalars,
  type Workflow,
  type WorkflowParam,
  type WorkflowParamAttributeMapping,
} from "../../../graphql.js";
import {
  type IPlaybookConfigState,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import { isNgsDataFileWorkflowParam } from "../../../redux/modules/playbook-execution.js";
import { getAttributeLabel } from "../../../redux/modules/sample-admin.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ATTRIBUTE_FILE_TYPES } from "../../../settings/settings.js";
import {
  COLUMN_SET,
  PLAYBOOK_TYPE,
  SAM_FILE_TYPE_OPTIONS,
  type WORKFLOW_PARAM_USAGE,
} from "../../../settings/settings.js";
import TabFence from "../../core/forms/TabFence.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import PlaybookScreenSection from "../PlaybookScreenSection.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import AttributesSelector from "./AttributesSelector.js";
import type { NfParamConfiguration } from "./NfParamsConfigurator.js";

type IPlaybookConfigSampleInfoScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface IPlaybookConfigSampleInfoScreenAppStateProps {
  globalFormError: string;
  globalFormWarning: string;
  selectedSampleAttributes: Partial<Attribute>[];
  sampleAttributes: Attribute[];
  sampleType: SampleType;
  attributeDefaults: Partial<RawAttributeDefault>[];
  selectedPlaybookType: PLAYBOOK_TYPE;
  chosenNgsDataAttribute: Attribute;
  nfPipelineWorkflowImportMappings: InputWorkflowParamAttributeMapping[];
  chosenNfPipelineParamMappings: any[];
  selectedWorkflow: Workflow;
  playbookFormState: Partial<IPlaybookConfigState>;
  hpcEnabled: boolean | null | undefined;
  cloudEnabled: boolean | null | undefined;
  nfParams: NfParamConfiguration[];
}

interface IPlaybookConfigSampleInfoScreenDispatchProps {
  updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => void;
  savePlaybook: () => Promise<Playbook>;
}

export interface IPlaybookConfigSampleInfoScreenProps
  extends IPlaybookConfigSampleInfoScreenAppStateProps,
    IPlaybookConfigSampleInfoScreenDispatchProps,
    IPlaybookConfigSampleInfoScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IPlaybookConfigSampleInfoScreenOwnProps,
): IPlaybookConfigSampleInfoScreenAppStateProps {
  const playbookFormState: Partial<IPlaybookConfigState> = state.playbookAdminState.playbookConfigState;
  const selectedWorkflow: Workflow = state.listing.approvedWorkflows.find(
    (workflow) => workflow.sourceId === playbookFormState.workflowId,
  );

  return {
    globalFormError: getFormError(PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    globalFormWarning: getFormWarning(PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    // selectedSampleType: state.playbooksState.playbookCreationState.sampleTypeId,
    selectedSampleAttributes: state.playbookAdminState.playbookConfigState.sampleAttributes,
    sampleAttributes: state.sampleAdmin.sampleType.sampleAttributes,
    sampleType: state.sampleAdmin.sampleType,
    attributeDefaults: state.sampleAdmin.attributeDefaults,
    selectedPlaybookType: state.playbookAdminState.playbookConfigState.playbookType,
    chosenNgsDataAttribute: formValueSelector(PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME)(
      state,
      PlaybookConfigSampleInfoScreenImpl.FIELD_NGS_DATA_ATTRIBUTE,
    ),
    nfPipelineWorkflowImportMappings: state.playbookAdminState.playbookConfigState.workflowImportMappings,
    chosenNfPipelineParamMappings: formValueSelector(PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME)(
      state,
      PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS,
    ),
    selectedWorkflow: selectedWorkflow,
    playbookFormState: playbookFormState,
    hpcEnabled: state.config.configuration.application?.hpcEnabled,
    cloudEnabled: state.config.configuration.application?.cloudEnabled,
    nfParams: state.playbookAdminState.playbookConfigState.nfParams,
  };
}

function mapDispatchToProps(dispatch): IPlaybookConfigSampleInfoScreenDispatchProps {
  return {
    updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => dispatch(updatePlaybookConfigState(state)),
    savePlaybook: () => dispatch(savePlaybook()),
  };
}

interface IPlaybookConfigSampleInfoScreenState {
  sampleAttributes: Attribute[];
  selectedSampleAttributes: IConfiguredAttribute[];
  attributeDefaults: Partial<RawAttributeDefault>[];
  attributesValidationError: string;
}

export class PlaybookConfigSampleInfoScreenImpl extends Component<
  IPlaybookConfigSampleInfoScreenProps,
  IPlaybookConfigSampleInfoScreenState
> {
  public static PLAYBOOK_CREATION_FORM_NAME = "PLAYBOOK_CREATION_SAMPLE_INFO";
  // field identifiers
  public static FIELD_NGS_DATA_ATTRIBUTE = "FIELD_NGS_DATA_ATTRIBUTE";

  public static FIELD_IMPORT_MAPPINGS = "PLAYBOOK_CREATION_FORM_FIELD_IMPORT_MAPPINGS";
  public static FIELD_IMPORT_MAPPINGS_WORKFLOWFIELD = "PLAYBOOK_CREATION_FORM_FIELD_IMPORT_MAPPINGS_WORKFLOWFIELD";
  public static FIELD_IMPORT_MAPPINGS_ATTRIBUTE = "PLAYBOOK_CREATION_FORM_FIELD_IMPORT_MAPPINGS_ATTRIBUTE";
  public static PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS = "PLAYBOOK_CREATION_NGS_RESULTS_MAPPING";
  public static PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD =
    "PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD";
  public static PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE = "PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE";

  private lastTabRef = createRef<Field>();
  private firstTabRef = createRef<Field>();

  constructor(props) {
    super(props);

    this.state = {
      sampleAttributes: [],
      selectedSampleAttributes: [],
      attributeDefaults: null,
      attributesValidationError: null,
    };
  }

  isLastScreen = () => {
    return (
      this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES &&
      !this.props.hpcEnabled &&
      !this.props.cloudEnabled
    );
  };

  initializeNgsDataAttribute = (attributes: Attribute[]) => {
    // init the mapping from file attributes to ngs raw data file workflow param

    let fileAttribute: Attribute;
    let fileAttributeMappingId: number;
    let requestedFileAttributeType: ATTRIBUTE_FILE_TYPES;

    // not required for nextflow playbooks
    if (this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      const nfParams: NfParamConfiguration[] = this.props.playbookFormState.nfParams;
      // exactly one workflow param input should correspond to some fitting input type (this is validated already in the previous screen)
      const selectedFileParam: NfParamConfiguration = nfParams
        .filter((nfParam) => !nfParam.hidden)
        .find(isRawDataFileTypeNfParam);

      if (!selectedFileParam) {
        this.props.updateWizardMessage(
          "You must configure one of the workflow parameters to accept files with aligned, unaligned or paired reads.",
        );
        return;
      }

      requestedFileAttributeType = getMatchingAttributeTypeFromNfParamType(selectedFileParam.type);
      fileAttribute = attributes?.find((attribute: Attribute) => attribute.fileType?.id === requestedFileAttributeType);

      // if an existing mapping exists, make sure the corresponding attribute still exists
      const savedFileAttributeMappingId = this.props.playbookFormState.workflowReadsAttributeId;
      fileAttributeMappingId =
        savedFileAttributeMappingId && attributes.find((a) => a.id === savedFileAttributeMappingId)
          ? savedFileAttributeMappingId
          : fileAttribute?.id; // any fitting attribute set by default ;
    } else {
      const filesWorkflowParam = this.props.selectedWorkflow?.workflowParams.find(isNgsDataFileWorkflowParam);
      if (!filesWorkflowParam) {
        this.props.updateWizardMessage(
          "The selected NGS workflow does not specify the file inputs that will be provided during Playbook execution. " +
            "Please review the approval settings for the workflow.",
        );
        return;
      }
      requestedFileAttributeType = getMatchingAttributeTypeFromParamUsage(
        filesWorkflowParam.usage as WORKFLOW_PARAM_USAGE,
      );
      fileAttribute = attributes?.find((attribute: Attribute) => attribute.fileType?.id === requestedFileAttributeType);

      const fileAttributeMapping = this.props.playbookFormState.workflowImportMappings?.find(
        (mapping) => mapping.workflowFieldId === filesWorkflowParam.name,
      );

      fileAttributeMappingId =
        fileAttributeMapping && attributes.find((a) => a.id === fileAttributeMapping.attributeId)
          ? fileAttributeMapping.attributeId
          : fileAttribute?.id; // fitting attribute set by default ; also makes sure the attribute corresponding to existing mapping still exists
    }

    if (!fileAttribute) {
      const expectedFileAttribtueTypeLabel = SAM_FILE_TYPE_OPTIONS.find(
        (option) => option.value === requestedFileAttributeType,
      )?.label;
      this.props.updateWizardMessage(
        `'${this.props.sampleType.name}' does not include an ${expectedFileAttribtueTypeLabel} file attribute to associate NGS data files as configured in the workflow.`,
      );
      return;
    }

    this.props.initialize({
      [PlaybookConfigSampleInfoScreenImpl.FIELD_NGS_DATA_ATTRIBUTE]: getSelectedOption<Attribute>(
        attributes,
        fileAttributeMappingId,
        (option: Attribute) => option.id,
      ),
    });
  };

  initializeNgsParamTrackerMappings = (attributes: Attribute[], nfParams: NfParamConfiguration[]) => {
    const mappings: any[] = [];
    this.props.nfPipelineWorkflowImportMappings?.forEach((m) => {
      const workflowField: MapperWorkflowField = nfParams
        .map((nfParam) => NfParamConfigToMapperWorkflowField(nfParam))
        .find((p) => p.name === m.workflowFieldId);
      const attribute: Attribute = attributes.find((a) => a.id === m.attributeId);
      // check if the workflow field is still file-related,
      // and if the (potentially modified) workflow field subtype still fits the attribute (for editing of config)
      if (
        workflowField &&
        attribute &&
        isRawDataFileSubType(workflowField.subType) &&
        attributeFileTypeMatchesNfParamSubType(attribute.fileType.id, workflowField.subType)
      ) {
        mappings.push({
          [PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE]: attribute,
          [PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD]: workflowField,
        });
      }
    });

    this.props.initialize({
      [PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS]: mappings,
    });
  };

  loadData = () => {
    const allAttributes: Attribute[] = this.props.sampleAttributes
      .filter((a: Attribute) => !a.automatic) // automatic attributes are never shown in the playbook forms (SEL6-3376)
      .filter(
        (a: Attribute) =>
          this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES || // for NF we allow also analytical attributes
          a.columnSet?.id !== COLUMN_SET.COS_ANALYTICAL_ID.id, // analytical attributes are also hidden in the sample registration form for all other playbook types
      );

    if (this.props.selectedPlaybookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      this.initializeNgsDataAttribute(allAttributes);
    }

    if (this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      this.initializeNgsParamTrackerMappings(allAttributes, this.props.nfParams);
    }

    const selectedAttributes: IConfiguredAttribute[] = fleshOutConfiguredAttributes(
      this.props.selectedSampleAttributes,
      allAttributes,
    );

    // build the initial selection, and add new mandatory elements (if necessary) to the current selection
    allAttributes
      .filter((attribute: Attribute) => attribute.mandatory)
      .forEach((mandatoryAttribute: Attribute) => {
        if (
          !selectedAttributes.find((selectedAttribute: Attribute) => selectedAttribute.id === mandatoryAttribute.id)
        ) {
          selectedAttributes.push(mandatoryAttribute);
        }
      });

    this.setState(
      {
        sampleAttributes: allAttributes,
        selectedSampleAttributes: selectedAttributes,
        attributeDefaults: this.props.attributeDefaults,
      },
      this.validateAttributes,
    );
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps: IPlaybookConfigSampleInfoScreenProps) {
    if (this.props.chosenNgsDataAttribute !== prevProps.chosenNgsDataAttribute) {
      // try to get the attribute from the selected sample attributes which corresponds to the chosen file attribute
      const selectedNgsDataAttribute: Attribute = this.state.selectedSampleAttributes.find(
        (a) => a.id === this.props.chosenNgsDataAttribute.id,
      );

      // prepare the new attribute selection
      let newSelectedSampleAttributes: Attribute[] = [...this.state.selectedSampleAttributes];

      // reset the mandatory state of the selected attributes if they differ from the original state
      newSelectedSampleAttributes = newSelectedSampleAttributes.map((s) => {
        const correspondingAttribute: Attribute = this.state.sampleAttributes.find((a) => a.id === s.id);
        return correspondingAttribute.mandatory === s.mandatory
          ? s
          : { ...s, mandatory: correspondingAttribute.mandatory };
      });

      if (selectedNgsDataAttribute) {
        // if the new file attribute is already in the selection, make it mandatory
        newSelectedSampleAttributes.find((a) => a.id === this.props.chosenNgsDataAttribute.id).mandatory = true;
      } else {
        // otherwise add it to the selection, as mandatory attribute
        newSelectedSampleAttributes.push({
          ...this.state.sampleAttributes.find(
            (sampleAttribute) => sampleAttribute.id === this.props.chosenNgsDataAttribute.id,
          ),
          mandatory: true,
        });
      }

      // store the adapted selection
      this.setState({
        selectedSampleAttributes: newSelectedSampleAttributes,
      });
    }

    // NF pipeline case, with multiple mappings:
    if (this.props.chosenNfPipelineParamMappings !== prevProps.chosenNfPipelineParamMappings) {
      // prepare the new attribute selection
      let newSelectedSampleAttributes: Attribute[] = [...this.state.selectedSampleAttributes];
      let isChanged = false;
      // get previously selected attribute IDs
      const previousAttributeIds = extractAttributeIdsFromMappingSelection(
        prevProps.chosenNfPipelineParamMappings,
        PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE,
      );
      // get newly selected attribute IDs
      const newAttributeIds = extractAttributeIdsFromMappingSelection(
        this.props.chosenNfPipelineParamMappings,
        PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE,
      );
      // build the diff
      const addedAttributeIds: number[] = newAttributeIds.filter((n) => !previousAttributeIds.includes(n));
      const droppedAttributeIds: number[] = previousAttributeIds.filter((n) => !newAttributeIds.includes(n));

      // there can only be one change at a time, so let's simplify the logic
      if (droppedAttributeIds.length === 1) {
        isChanged = true;
        // reset the mandatory state of the selected attributes if they differ from the original state
        newSelectedSampleAttributes = newSelectedSampleAttributes.map((s) => {
          if (s.id === droppedAttributeIds[0]) {
            const correspondingAttribute: Attribute = this.state.sampleAttributes.find((a) => a.id === s.id);
            return correspondingAttribute.mandatory === s.mandatory
              ? s
              : { ...s, mandatory: correspondingAttribute.mandatory };
          } else {
            return s;
          }
        });
      }
      if (addedAttributeIds.length === 1) {
        isChanged = true;
        // if already selected, make it mandatory
        if (newSelectedSampleAttributes.find((a) => a.id === addedAttributeIds[0])) {
          newSelectedSampleAttributes = newSelectedSampleAttributes.map((s) => {
            return s.id === addedAttributeIds[0] ? { ...s, mandatory: true } : s;
          });
        } else {
          // if not selected yet, add it as mandatory
          const newAttribute = this.state.sampleAttributes.find((a) => a.id === addedAttributeIds[0]);
          newSelectedSampleAttributes.push({ ...newAttribute, mandatory: true });
        }
      }

      // if there was a change, save the new set of selected attributes
      if (isChanged) {
        this.setState({
          selectedSampleAttributes: newSelectedSampleAttributes,
        });
      }
    }
  }

  validateAttributes = () => {
    // validation
    if (this.state.selectedSampleAttributes.length === 0) {
      this.setState({
        attributesValidationError: "Select the sample attributes to be available on the Playbook as input fields",
      });
    } else {
      this.setState({ attributesValidationError: null });
    }
  };

  handleSubmitData = (values) => {
    let mappingsPayload: WorkflowParamAttributeMapping[] = [];
    let playbookCreationState: Partial<IPlaybookConfigState>;

    if (this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      // we have one or more mappings for the NGS file data INPUT
      const mappings: any[] = values[PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS];

      mappingsPayload = mappings.map((mapping) => {
        return {
          attributeId: mapping[PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_ATTRIBUTE].id,
          workflowFieldId:
            mapping[PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS_WORKFLOWFIELD].name,
        };
      });
      playbookCreationState = {
        workflowImportMappings: mappingsPayload,
        sampleAttributes: this.state.selectedSampleAttributes,
      };
    } else {
      const fileAttribute: Attribute = values[PlaybookConfigSampleInfoScreenImpl.FIELD_NGS_DATA_ATTRIBUTE];

      if (this.props.selectedPlaybookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
        const filesWorkflowParam: WorkflowParam =
          this.props.selectedWorkflow?.workflowParams.find(isNgsDataFileWorkflowParam);
        mappingsPayload = fileAttribute
          ? [
              {
                workflowFieldId: filesWorkflowParam.name,
                attributeId: fileAttribute.id,
              },
            ]
          : [];

        this.validate(mappingsPayload, this.props, this.state);
      }
      playbookCreationState = {
        workflowImportMappings: mappingsPayload,
        workflowReadsAttributeId: fileAttribute?.id, // required for combined Nextflow playbooks
        sampleAttributes: this.state.selectedSampleAttributes,
      };
    }

    this.props.updatePlaybookCreationState(playbookCreationState);

    return this.isLastScreen()
      ? this.props
          .savePlaybook()
          .then(() => this.props.proceedWithNextStep())
          .catch((error: Error) => {
            throw new SubmissionError({ _error: error.message });
          })
      : this.props.proceedWithNextStep();
  };

  attributesUpdated = (selectedSampleAttributes: IConfiguredAttribute[]) => {
    this.setState({ selectedSampleAttributes: selectedSampleAttributes }, this.validateAttributes);
  };

  render() {
    const availableNfFileParameters: MapperWorkflowField[] =
      this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES
        ? this.props.nfParams
            .filter((nfParam) => !nfParam.hidden && nfParam.type.type === NfParamTypeEnum.File)
            .filter(isRawDataFileTypeNfParam)
            .map((nfParam) => NfParamConfigToMapperWorkflowField(nfParam))
        : [];

    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error || this.state.attributesValidationError,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: this.isLastScreen() ? "Save Playbook" : "Next",
            isLoading: !!this.props.asyncValidating || (this.isLastScreen() ? this.props.submitting : false),
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form
          className="form-horizontal sample-info-screen ngs-data-screen"
          onSubmit={this.props.handleSubmit(this.handleSubmitData)}
        >
          <div onKeyDownCapture={handleTabInForm}>
            <TabFence targetRef={this.lastTabRef} />

            {this.props.selectedPlaybookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES && (
              <>
                <PlaybookScreenSection
                  title="NGS Data"
                  description="The user will need to provide data files for NGS analysis. Select the sample attribute that is associated with this data."
                  noPadding={true}
                />
                <ReactSelectReduxFormField
                  key={PlaybookConfigSampleInfoScreenImpl.FIELD_NGS_DATA_ATTRIBUTE}
                  name={PlaybookConfigSampleInfoScreenImpl.FIELD_NGS_DATA_ATTRIBUTE}
                  horizontalLayout={true}
                  isClearable={false}
                  isSearchable={false}
                  label="File attribute"
                  component={ReactSelectField}
                  options={this.state.sampleAttributes.filter(isNgsDataFileAttribute)}
                  getOptionValue={(option: Attribute) => option.id}
                  getOptionLabel={getAttributeLabel}
                  noOptionsMessage={() => "No available file attribute..."}
                  validate={simpleDropdownFieldValidation}
                />
                <br /> <br />
              </>
            )}

            {this.props.selectedPlaybookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES && (
              <>
                <PlaybookScreenSection
                  title="NGS Data"
                  description="The user will need to provide data files for NGS analysis. Select the workflow parameters and related sample attributes providing the files."
                  noPadding={true}
                />
                <ExportedWorkflowFieldMapperReduxFormField
                  name={PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_FIELD_MAPPINGS}
                  component={ExportedWorkflowFieldMapper}
                  availableExportFields={availableNfFileParameters}
                  isLoadingAttributes={false}
                  sampleAttributes={this.state.sampleAttributes.filter(isNgsDataFileAttribute)}
                  validate={PlaybookConfigSampleInfoScreenImpl.workflowFieldMappingValidator}
                  isInputMappingContext={true}
                />
                <br /> <br />
              </>
            )}

            <PlaybookScreenSection
              title="Sample Information"
              description="You can configure the Playbook so that the user provides information for each sample. Select the attributes that the users will populate during Playbook execution."
              noPadding={true}
            />

            <div className="playbook-step-custom form-group">
              <label className="control-label">
                {`${
                  this.state.selectedSampleAttributes.length > 0 ? this.state.selectedSampleAttributes.length : "No "
                } sample attribute(s) selected`}
              </label>
              <AttributesSelector
                isLoadingAttributes={false}
                selectedAttributes={this.state.selectedSampleAttributes}
                availableAttributes={this.state.sampleAttributes}
                attributeDefaults={this.state.attributeDefaults}
                attributesUpdated={this.attributesUpdated}
                dropdownLabel="Add sample attribute..."
              />
            </div>
            <TabFence targetRef={this.firstTabRef} />
          </div>
        </Form>
      </PlaybookWizardStep>
    );
  }

  static workflowFieldMappingValidator: Validator = (mappings): string | undefined => {
    // at least one complete mapping needs to be provided
    if (!mappings?.length || (mappings?.length === 1 && Object.keys(mappings[0]).length < 2)) {
      return "At least one Nextflow parameter must be mapped to a sample attribute.";
    }
    return undefined;
  };

  validate = (
    mappings: WorkflowParamAttributeMapping[],
    props: IPlaybookConfigSampleInfoScreenProps,
    state: IPlaybookConfigSampleInfoScreenState,
  ) => {
    // we will make sure that the file attributes that have been chosen for hosting the NGS Data files
    // will also be part of the sample input attributes during playbook execution
    if (mappings?.length) {
      const missingVisibleAttributes: Attribute[] = mappings
        .filter((mapping: WorkflowParamAttributeMapping) => {
          return !state.selectedSampleAttributes.find((attribute) => attribute.id === mapping.attributeId);
        })
        .map((mapping: WorkflowParamAttributeMapping) => {
          return props.sampleAttributes.find((attribute) => attribute.id === mapping.attributeId);
        });
      if (missingVisibleAttributes.length > 0) {
        throw new SubmissionError({
          _error: selMessage({ id: "playbook.execution.error.hiddenFileAttribute" }).toString(),
        });
      }
    }
  };
}

const PlaybookConfigSampleInfoScreen = connect<
  IPlaybookConfigSampleInfoScreenAppStateProps,
  IPlaybookConfigSampleInfoScreenDispatchProps,
  IPlaybookConfigSampleInfoScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: PlaybookConfigSampleInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME,
    // validate: PlaybookCreationSampleInfoScreenImpl.validate, // for global validation
    destroyOnUnmount: false,
  })(PlaybookConfigSampleInfoScreenImpl) as any,
);

export { PlaybookConfigSampleInfoScreen };
